import type { InteractiveAuthError } from "@b2bportal/card-api";
import {
  type ErrorCode,
  type FulfillSuccess,
  Product,
  type PurchaseError,
  type TrackingProperties,
} from "@b2bportal/purchase-api";
import { type ChooseSavingsProperties, ProductType } from "@hopper-b2b/types";
import { combineTrackingProperties } from "@hopper-b2b/utilities";
import { type DoneInvokeEvent, type State, assign } from "xstate";
import {
  AirCfarSelectorsV1,
  AirChfarSelectors,
  AirDisruptionSelectorsV1,
  AirPFExerciseSelectors,
  FlightSelectors,
  LodgingSelectors,
} from "../states";
import { getCartBreakdownProductTypes } from "../states/Cart/CartQuote/selectors";
import {
  type CartFulfillErrorModal,
  ParentState,
  type PartialParentContext,
} from "../types";
import { trackCheckoutEvent } from "./sink";

type ErrorEvent = {
  data: Array<PurchaseError | InteractiveAuthError>;
  type: CartFulfillErrorModal;
};

interface TrackFulfillEventProps<Context> {
  eventName: string;
  additionalProperties?: Record<string, string | number | boolean | string[]>;
  getAdditionalProperties?: (
    context: Context,
    event: DoneInvokeEvent<FulfillSuccess | ErrorEvent>
  ) => { [key: string]: string | number | boolean | string[] };
  getAdditionalEncryptedProperties?: (
    context: Context,
    event: DoneInvokeEvent<FulfillSuccess | ErrorEvent>
  ) => string[];
}

export function trackPollSuccess<Context extends PartialParentContext>({
  eventName,
  additionalProperties,
  getAdditionalProperties,
  getAdditionalEncryptedProperties,
}: TrackFulfillEventProps<Context>) {
  return assign((context: Context, event: DoneInvokeEvent<FulfillSuccess>) => {
    const products = getCartBreakdownProductTypes({ context });

    const genericFulfillTrackingProperties = event.data.trackingProperties;
    const fulfilledProducts = event.data.products;
    const quoteTrackingProperties =
      context[ParentState.cartQuote]?.trackingProperties;
    const quoteBreakdown = context[ParentState.cartQuote]?.quoteBreakdown;

    const quotedProductTrackingProperties = quoteBreakdown?.products?.map(
      (p) =>
        p.product.value.trackingPropertiesV2 ||
        p.product.value.trackingProperties
    );

    // Products for which we fetch tracking properties
    // For Disruption, we set its tracking properties even when it's not attached to the cart order. See `getDisruptionFulfillTrackingProperties`
    const trackingProducts = [
      ...new Set([
        ...(products ?? []),
        Product.AirDisruption,
        Product.AirInsurance,
      ]),
    ];

    // Xstate product tracking properties from previous context values
    const arrayOfTrackingProperties = getProductTrackingProperties<Context>(
      trackingProducts,
      context
    );
    // Fulfilled products' tracking properties
    const fulfilledProductTrackingProperties = fulfilledProducts?.map(
      (product) => {
        return product.value.trackingProperties;
      }
    );

    let chooseSavingsProperties: ChooseSavingsProperties[] = [];

    if (
      products.some(
        (p) => p === Product.Flight || p === Product.MultiProviderFlight
      )
    ) {
      chooseSavingsProperties = [getChooseSavingsProperties({ context })];
    }

    // Combine injected properties
    const customProperties = {
      ...(additionalProperties || {}),
      ...(getAdditionalProperties?.(context, event) || {}),
    };
    const customEncryptedProperties =
      getAdditionalEncryptedProperties?.(context, event) || [];

    // Combine all BE tracking properties
    const { properties: beProperties, encryptedProperties: beEncrypted } =
      combineTrackingProperties(
        [
          ...(quoteTrackingProperties ? [quoteTrackingProperties] : []),
          ...(quotedProductTrackingProperties || []),
          ...arrayOfTrackingProperties,
          fulfilledProductTrackingProperties || [],
          genericFulfillTrackingProperties || [],
          ...chooseSavingsProperties,
        ].flat(),
        {},
        customEncryptedProperties
      );

    // FE custom properties override BE properties
    const properties = {
      ...beProperties,
      ...customProperties,
    };

    trackCheckoutEvent({
      eventName,
      properties: { success: true, ...properties },
      encryptedProperties: beEncrypted,
    });
    return context;
  });
}

export function trackPollFailure<Context extends PartialParentContext>({
  eventName,
  additionalProperties,
  getAdditionalProperties,
  getAdditionalEncryptedProperties,
}: TrackFulfillEventProps<Context>) {
  return assign(
    (
      context: Context,
      event: DoneInvokeEvent<{
        data: Array<PurchaseError | InteractiveAuthError>;
        type: CartFulfillErrorModal;
      }>
    ) => {
      const products = getCartBreakdownProductTypes({ context });
      const quoteTrackingProperties =
        context[ParentState.cartQuote]?.trackingProperties;
      const quoteBreakdown = context[ParentState.cartQuote]?.quoteBreakdown;

      const quotedProductTrackingProperties = quoteBreakdown?.products?.map(
        (p) =>
          p.product.value.trackingPropertiesV2 ||
          p.product.value.trackingProperties
      );

      const responseErrors = event?.data?.data;
      const stringifiedErrors =
        responseErrors?.length > 0
          ? responseErrors
              .map((error) => {
                if ("string" === typeof error) {
                  return error;
                }

                return (error as ErrorCode)?.code
                  ? (error as ErrorCode).code
                  : (error as any).Error || "ProductError";
              })
              .join(" - ")
          : "Poll Finalized response returned an error and the given error code is not handled.";
      // Xstate product tracking properties from previous context values
      const arrayOfTrackingProperties = getProductTrackingProperties<Context>(
        products,
        context
      );

      // Combine injected properties
      const customProperties = {
        ...(additionalProperties || {}),
        ...(getAdditionalProperties?.(context, event) || {}),
      };

      const customEncryptedProperties =
        getAdditionalEncryptedProperties?.(context, event) || [];

      const { properties: beProperties, encryptedProperties: beEncrypted } =
        combineTrackingProperties(
          [
            ...(quoteTrackingProperties ? [quoteTrackingProperties] : []),
            ...(quotedProductTrackingProperties || []),
            ...arrayOfTrackingProperties,
          ].flat(),
          {},
          customEncryptedProperties
        );

      // FE custom properties override BE properties
      const properties = {
        ...beProperties,
        ...customProperties,
      };

      trackCheckoutEvent({
        eventName,
        properties: {
          success: false,
          failure_reason: stringifiedErrors,
          ...properties,
        },
        encryptedProperties: beEncrypted,
      });
      return context;
    }
  );
}

export function getProductTrackingProperties<Context>(
  products: Array<Product>,
  context: Context
): Array<TrackingProperties> {
  return (
    products?.map((product) => {
      const productSelector = mapOfProductsToTrackingPropertySelectors[product];
      return productSelector
        ? productSelector({ context })
        : { properties: {} };
    }) || []
  );
}

// Map of xstate selectors to products' fulfill tracking properties
const mapOfProductsToTrackingPropertySelectors: Record<
  Product,
  (state: Pick<State<any>, "context">) => TrackingProperties
> = {
  [Product.AirPriceFreeze]:
    AirPFExerciseSelectors.getCompleteBuyAirPriceFreezeProperties,
  [Product.AirCfar]: AirCfarSelectorsV1.getCompleteBuyAirCfarProperties,
  [Product.AirChfar]: AirChfarSelectors.getCompleteBuyAirChfarProperties,
  [Product.AirDisruption]:
    AirDisruptionSelectorsV1.getCompleteBuyAirDisruptionTrackingProperties,
  [Product.AirPriceDrop]: () => null,
  [Product.Flight]: FlightSelectors.getFlightFulfillTrackingProperties,
  [Product.MultiProviderFlight]:
    FlightSelectors.getFlightFulfillTrackingProperties,
  [Product.Seats]: () => null, // NOT IMPLEMENTED,
  [Product.AgentFee]: () => null, // NOT IMPLEMENTED
  [Product.Ground]: () => null, // NOT IMPLEMENTED
  [Product.Homes]: () => null,
  [Product.Hotel]: LodgingSelectors.getLodgingFulfillTrackingProperties,
  [Product.LodgingCfar]: () => null, // NOT IMPLEMENTED
  [Product.LodgingPriceFreeze]: () => null, // NOT IMPLEMENTED
  [Product.VipSupport]: () => null, // NOT IMPLEMENTED
  [Product.Incentives]: () => null,
  [Product.AirInsurance]: () => null, // NOT IMPLEMENTED,
  [Product.Package]: () => null, // NOT IMPLEMENTED
  [Product.AirBaggage]: () => null, // NOT IMPLEMENTED
  [Product.PriceFreeze]: () => null, // NOT IMPLEMENTED @PF-TODO – What do we need to include here?
  [Product.LodgingInsurance]: () => null, // NOT IMPLEMENTED
  [Product.VirginAuVffPoints]: () => null, // NOT IMPLEMENTED
  [Product.Experiences]: () => null, // NOT IMPLEMENTED,
  [Product.Cart]: () => null, // NOT IMPLEMENTED,
  [Product.Activities]: () => null, // NOT IMPLEMENTED,
  [Product.HtsAirCfar]: () => null, // NOT IMPLEMENTED,
  [Product.HtsAirDisruption]: () => null, // NOT IMPLEMENTED,
  [Product.HtsLodgingCfar]: () => null, // NOT IMPLEMENTED,
  [Product.CfarHts]: () => null, // NOT IMPLEMENTED
  [Product.DisruptionHts]: () => null, // NOT IMPLEMENTED
  [Product.RbcInsurance]: () => null, // NOT IMPLEMENTED
  [Product.RbcPoints]: () => null, // NOT IMPLEMENTED
};

export const getCreditUsed = (context): number => {
  if (!context[ParentState.wallet]) {
    return undefined;
  }

  const { creditOffer, offer } = context[ParentState.wallet];

  const calculateCreditUsageValue = () => {
    const flightTotal =
      context.flightShop.shopPricingInfo?.fare?.[0]?.pricing?.total;

    const voucherDiscount = offer?.amount;

    if (flightTotal && creditOffer) {
      const value =
        (flightTotal?.fiat?.value ?? 0) +
        (voucherDiscount?.fiat?.value ?? 0) +
        (creditOffer?.amount?.fiat?.value ?? 0);

      // voucherDiscount && creditOffer are negative amounts while flightTotal is positive
      if (value < 0) {
        return Math.abs((creditOffer.amount?.fiat?.value ?? 0) - value);
      } else {
        return Math.abs(creditOffer.amount?.fiat?.value ?? 0);
      }
    } else {
      return 0;
    }
  };

  const credit = {
    ...creditOffer?.amount,
    fiat: {
      ...creditOffer?.amount.fiat,
      value: calculateCreditUsageValue() || 0,
    },
  };

  return credit?.fiat?.value ?? 0;
};

export const getChooseSavingsProperties = ({
  context,
}): ChooseSavingsProperties => {
  if (
    !context[ParentState.wallet]?.creditOffer &&
    !context[ParentState.wallet]?.offer
  ) {
    return {
      combined_discount_usd: 0,
      applicable_combined_discount_usd: 0,
      credits_amount_usd: 0,
      applicable_credits_amount_usd: 0,
      product_type: ProductType.FLIGHTS,
    };
  }

  const creditOfferDiscount =
    Math.abs(context[ParentState.wallet].creditOffer?.amount?.fiat?.value) ?? 0;
  const voucherDiscount =
    Math.abs(context[ParentState.wallet].offer?.amount.fiat.value) ?? 0;

  const creditUsed = getCreditUsed(context);

  const totalPrice =
    context.flightShop.shopPricingInfo?.fare?.[0]?.pricing?.total?.fiat
      ?.value ?? 0;

  const combinedDiscount = creditOfferDiscount + voucherDiscount;

  return {
    combined_discount_usd: combinedDiscount,
    applicable_combined_discount_usd: Math.min(totalPrice, combinedDiscount),
    credits_amount_usd: creditOfferDiscount,
    applicable_credits_amount_usd: parseFloat(creditUsed.toFixed(2)),
    product_type: ProductType.FLIGHTS,
  };
};

export * from "./sink";
